import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Link } from "@tanstack/react-router"
import { ChevronDown, Loader2, UserPlus, ShieldCheck, Lock, Truck, Activity, ArrowLeft } from "lucide-react"
import { useState } from "react"

import {
  ACCOUNT_TYPES,
  signupSchema,
  type SignupFormValues,
} from "@/lib/schemas/auth.schema"
import { useSignup } from "@/hooks/auth/useSignup"
import { Button } from "@/components/ui/button"
import { useDevice } from "@/hooks/useIsMobile"
import { cn } from "@/lib/utils"

import { AuthLeftPanel } from "./AuthLeftPanel"

export function SignupForm() {
  const { isDesktop } = useDevice()
  const [showPassword, setShowPassword] = useState(false)
  const signup = useSignup()

  const searchParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "")
  const rawRole = searchParams.get("role") || searchParams.get("accountType")
  const initialRole = (rawRole?.toUpperCase() === "SELLER" ? "SELLER" : "BUYER") as SignupFormValues["role"]

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    mode: "onTouched",
    defaultValues: { role: initialRole, name: "", email: "", password: "" },
  })

  const onSubmit = (values: SignupFormValues) => {
    signup.mutate(values)
  }

  return (
    <div className="min-h-[100dvh] lg:min-h-screen flex flex-col lg:flex-row w-full bg-[#f5f5f5]">
      
      {/* Left Panel: Branding & Benefits */}
      {isDesktop && <AuthLeftPanel />}

      {/* Right Panel: Signup Form */}
      <main className="w-full lg:w-1/2 bg-[#f5f5f5] flex flex-col h-[100dvh] lg:h-screen p-4 sm:p-6 overflow-y-auto">
        {/* Top-Left Back Navigation (Positioned at the top of the page on Mobile & Tablet, Hidden on Desktop) */}
        <div className="w-full lg:hidden shrink-0">
          <Link
            to="/"
            className="group inline-flex items-center gap-1.5 text-xs font-semibold text-secondary hover:text-[#be001e] transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#be001e]/30 rounded-md p-1"
          >
            <ArrowLeft className="size-3.5 transition-transform duration-200 group-hover:-translate-x-1" />
            <span>Back to Home</span>
          </Link>
        </div>

        {/* Signup Card Container (Centered with space between top navigation and card) */}
        <div className="w-full max-w-[380px] mx-auto my-auto py-6 pb-20 sm:pb-8 lg:py-0">
          <div className="bg-white w-full rounded-xl p-6 border-05 border-outline-variant flex flex-col gap-4.5 shadow-sm">
          {/* Form Header */}
          <header className="text-center md:text-left">
            <h1 className="font-sans text-xl font-black text-on-surface">Create an account</h1>
          </header>



          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-3.5">
            
            {/* Account Type/Role */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-secondary" htmlFor="signup-role">
                Role
              </label>
              <div className="relative">
                <select
                  id="signup-role"
                  aria-invalid={!!errors.role}
                  className="w-full h-9 px-3 pr-10 border-05 border-outline-variant rounded-lg text-xs bg-surface text-on-surface appearance-none focus:outline-none focus:border-primary focus:ring-0 cursor-pointer"
                  {...register("role")}
                >
                  {ACCOUNT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute inset-y-0 right-3 my-auto size-3.5 text-secondary" />
              </div>
              {errors.role && (
                <p className="text-[11px] text-primary font-medium">{errors.role.message}</p>
              )}
            </div>

            {/* Full Name */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-secondary" htmlFor="signup-name">
                Full name
              </label>
              <input
                id="signup-name"
                type="text"
                placeholder="Jane Doe"
                autoComplete="name"
                aria-invalid={!!errors.name}
                className={cn(
                  "w-full h-9 px-3 border-05 border-outline-variant rounded-lg text-xs bg-surface text-on-surface focus:outline-none focus:border-primary focus:ring-0 transition-colors",
                  errors.name && "border-primary"
                )}
                {...register("name")}
              />
              {errors.name && (
                <p className="text-[11px] text-primary font-medium">{errors.name.message}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-secondary" htmlFor="signup-email">
                Email address
              </label>
              <input
                id="signup-email"
                type="email"
                placeholder="name@gmail.com"
                autoComplete="email"
                aria-invalid={!!errors.email}
                className={cn(
                  "w-full h-9 px-3 border-05 border-outline-variant rounded-lg text-xs bg-surface text-on-surface focus:outline-none focus:border-primary focus:ring-0 transition-colors",
                  errors.email && "border-primary"
                )}
                {...register("email")}
              />
              {errors.email && (
                <p className="text-[11px] text-primary font-medium">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="flex flex-col gap-1 relative">
              <label className="text-[10px] font-bold uppercase tracking-wider text-secondary" htmlFor="signup-password">
                Password
              </label>
              <div className="relative">
                <input
                  id="signup-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  aria-invalid={!!errors.password}
                  className={cn(
                    "w-full h-9 px-3 pr-12 border-05 border-outline-variant rounded-lg text-xs bg-surface text-on-surface focus:outline-none focus:border-primary focus:ring-0 transition-colors",
                    errors.password && "border-primary"
                  )}
                  {...register("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-primary uppercase tracking-wider hover:underline focus:outline-none cursor-pointer"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {errors.password && (
                <p className="text-[11px] text-primary font-medium">{errors.password.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              id="signup-submit"
              type="submit"
              disabled={signup.isPending}
              className="w-full h-9 bg-primary-container text-on-primary-container font-semibold rounded-lg text-xs transition-all duration-200 active:scale-[0.98] flex items-center justify-center hover:bg-primary-container/95 cursor-pointer mt-1"
            >
              {signup.isPending ? (
                <Loader2 className="size-3.5 mr-1.5 animate-spin" />
              ) : (
                <UserPlus className="size-3.5 mr-1.5" />
              )}
              {signup.isPending ? "Creating account…" : "Create account"}
            </Button>
          </form>


          {/* Sign in footer link */}
          <footer className="text-center">
            <p className="text-secondary text-xs">
              Already have an account?{" "}
              <Link
                to="/login"
                className="font-bold text-primary hover:underline"
              >
                Sign in
              </Link>
            </p>
          </footer>
        </div>
      </div>
    </main>

    </div>
  )
}
